import { apiHelper } from "@/utils";
import { API } from "@/constants";

export const lookupService = {
  getCertificates,
  getCountries,
  getRoles,
  getProductCategories,
  getProductConditions,
  getOrderStatuses,
  getStates,
  getArticleCategories,
  getArticleTypes,
  getLocations,
  getCompanies,
  getCompanyTypes,
  getGroups,
  getOfficeTypes,
  getPositions,
  getCompanyDepartmentOffices,
  getNotificationChannels,
  getNotificationIcons,
  getDocumentCategories,
  getCatalogSubcategories,
  getCatalogCategories,
  getUsers,
  getUserEmails,
  getPropertyAssets,
  getPropertyTypes,
  getPropertyStatuses,
  getPropertyTenures,
  getPropertyCategories,
  getPropertyAreaUnits,
  getPropertyActions,
  getOffices,
  getAmenities,
  getMeetingRooms,
  getDepartments,
  getCandidateJobs,
  getCandidateStatuses,
  getPanelTypes,
  getWorkgroups,
  getOrientationModuleHeaders,
};

const MODULE = {
  COUNTRY: "countries",
  STATE: "states",
  LOCATION: "locations",
  COMPANY: "companies",
  GROUP: "groups",
  COMPANY_TYPE: "company-types",
  OFFICE_TYPE: "office-types",
  POSITION: "positions",
  COMPANY_DEPARTMENT_OFFICE: "company-department-offices",
  PROPERTY_ASSET: "property-assets",
  PROPERTY_ACTION: "property-actions",
  PROPERTY_AREA_UNIT: "property-area-units",
  PROPERTY_CATEGORY: "property-categories",
  PROPERTY_STATUS: "property-statuses",
  PROPERTY_TENURE: "property-tenures",
  PROPERTY_TYPE: "property-types",
  DEPARTMENT: "departments",
  CANDIDATE_JOB: "candidate-jobs",
  CANDIDATE_STATUS: "candidate-statuses",
  PANEL_TYPE: "panel-types",
  WORKGROUP: "workgroups",
  ORIENTATION_MODULE_HEADER: "orientation-module-headers",
  USER_EMAIL: "user-emails",
};

const API_URL = API.BASE_URL_API + "/v1";

export function getOrderStatuses(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/order-statuses", { params });
}

export function getProductConditions() {
  const baseURL = API.BASE_URL_API.replace(/\/api\/?$/, "/app-api") + "/v1";
  return apiHelper(baseURL).get("/lookup/product-conditions");
}

export function getProductCategories(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/product-categories", { params });
}

export function getRoles() {
  return apiHelper(API_URL).get("/lookup/roles");
}

export function getCountries() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.COUNTRY}`);
}

export function getStates() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.STATE}`);
}

export function getArticleCategories(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/article-categories", { params });
}

export function getArticleTypes() {
  return apiHelper(API_URL).get("/lookup/article-types");
}

export function getCompanies(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/companies", { params });
}

export function getCompanyTypes() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.COMPANY_TYPE}`);
}

export function getGroups() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.GROUP}`);
}

export function getLocations() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.LOCATION}`);
}

export function getOfficeTypes() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.OFFICE_TYPE}`);
}

export function getPositions() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.POSITION}`);
}

export function getCompanyDepartmentOffices() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.COMPANY_DEPARTMENT_OFFICE}`);
}

export function getNotificationChannels() {
  return apiHelper(API_URL).get("/lookup/notification-channels");
}

export function getNotificationIcons() {
  return apiHelper(API_URL).get("/lookup/notification-icons");
}

export function getDocumentCategories(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/document-categories", { params });
}

export function getCatalogSubcategories(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/catalog-subcategories", { params });
}

export function getCatalogCategories(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/catalog-categories", { params });
}

export function getUsers({ companyUUID, searchWords = [] } = {}) {
  const params = {
    company_uuid: companyUUID || undefined,
    search_words: searchWords.length ? searchWords : undefined,
  };
  return apiHelper(API_URL).get("/lookup/users", { params });
}

export function getUserEmails(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get(`/lookup/${MODULE.USER_EMAIL}`, { params });
}

export function getPropertyAssets() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_ASSET}`);
}

export function getPropertyActions() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_ACTION}`);
}

export function getPropertyAreaUnits() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_AREA_UNIT}`);
}

export function getPropertyCategories() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_CATEGORY}`);
}

export function getPropertyStatuses() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_STATUS}`);
}

export function getPropertyTenures() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_TENURE}`);
}

export function getPropertyTypes() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PROPERTY_TYPE}`);
}

export function getOffices({ companyUUID, searchWords = [] } = {}) {
  const params = {
    company_uuid: companyUUID || undefined,
    search_words: searchWords.length ? searchWords : undefined,
  };
  return apiHelper(API_URL).get("/lookup/offices", { params });
}

export function getAmenities(searchWords = []) {
  const params = searchWords.length ? { search_words: searchWords } : {};
  return apiHelper(API_URL).get("/lookup/amenities", { params });
}

export function getMeetingRooms({
  companyUUID,
  officeUUID,
  searchWords = [],
} = {}) {
  const params = {
    company_uuid: companyUUID || undefined,
    office_uuid: officeUUID || undefined,
    search_words: searchWords.length ? searchWords : undefined,
  };
  return apiHelper(API_URL).get("/lookup/meeting-rooms", { params });
}

export function getDepartments() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.DEPARTMENT}`);
}

export function getCandidateJobs() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.CANDIDATE_JOB}`);
}

export function getCandidateStatuses() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.CANDIDATE_STATUS}`);
}

export function getPanelTypes() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.PANEL_TYPE}`);
}

export function getWorkgroups(filter = {}) {
  console.log("workgroup filter", filter.usageType);
  const request = {
    params: {
      usage_type: filter?.usageType ?? "",
    },
  };
  return apiHelper(API_URL).get(`/lookup/${MODULE.WORKGROUP}`, request);
}

export function getOrientationModuleHeaders() {
  return apiHelper(API_URL).get(`/lookup/${MODULE.ORIENTATION_MODULE_HEADER}`);
}

export function getCertificates() {
  return apiHelper(API_URL).get("/lookup/certs");
}
